import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { showToast } from '@/lib/custom-toast';
import { Send, Mail, Gem, Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Footer from '@/components/Footer';
import axios from 'axios';
import { API, AuthContext } from '@/App';

const Contact = () => {
  const { user } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const isProUser = user?.subscription_tier === 'pro' && (user?.subscription_status === 'active' || user?.subscription_status === 'trialing');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      showToast.success('¡Mensaje enviado! Te responderemos pronto.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al enviar el mensaje. Por favor, intenta de nuevo.';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      {/* Header */}
      <header className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-2.5 hover:opacity-75 transition-opacity duration-150 flex-shrink-0">
              <img 
                src="/icon-192x192.png" 
                alt="PGastos" 
                className="h-7 sm:h-8 w-auto"
              />
              <span className="text-base sm:text-lg font-bold text-stone-900 tracking-tight">PGastos</span>
            </Link>
            <Link to="/">
              <Button variant="outline" size="sm" className="border-stone-300 hover:bg-stone-100 text-stone-700 text-xs sm:text-sm active:scale-95 transition-all duration-150 ease-spring cursor-pointer">
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Volver
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 w-full">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-widest text-stone-400 mb-3">Soporte y Atención</p>
          <h1 className="text-3xl sm:text-5xl font-black text-stone-900 mb-4 tracking-tight">
            Contáctanos
          </h1>
          <p className="text-base sm:text-lg text-stone-600 max-w-lg mx-auto font-medium">
            ¿Tienes dudas, sugerencias o necesitas asistencia? Estamos para ayudarte.
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-8 items-start">
          {/* Contact Form */}
          <Card className="md:col-span-7 p-6 sm:p-8 bg-white border border-stone-200 shadow-sm rounded-3xl">
            <h2 className="text-xl font-bold text-stone-900 mb-1 tracking-tight">Envíanos un mensaje</h2>
            <p className="text-xs sm:text-sm text-stone-500 mb-6 font-medium">Te responderemos directo a tu correo.</p>
            
            {/* Badge de Soporte Prioritario */}
            {isProUser && (
              <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-2xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gem className="w-4 h-4 text-indigo-600" />
                  <span className="font-bold text-indigo-950 text-xs sm:text-sm">Soporte Prioritario Pro Activo</span>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-indigo-700 font-medium">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Respuesta prioritaria</span>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-stone-700">Nombre completo</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="mt-1.5 h-11 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-stone-700">Correo electrónico</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="mt-1.5 h-11 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="subject" className="text-xs font-bold uppercase tracking-wider text-stone-700">Asunto</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="¿En qué podemos ayudarte?"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="mt-1.5 h-11 border-stone-200 focus:border-indigo-600 focus:ring-indigo-600"
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-stone-700">Mensaje</Label>
                <textarea
                  id="message"
                  name="message"
                  rows="4"
                  placeholder="Escribe tu mensaje aquí..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="mt-1.5 w-full p-3 border border-stone-200 rounded-xl focus:outline-none focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 text-sm font-medium text-stone-800"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold h-12 rounded-xl active:scale-95 hover:scale-[1.01] transition-all duration-150 ease-spring shadow-sm cursor-pointer"
              >
                {loading ? 'Enviando...' : 'Enviar Mensaje'}
                <Send className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </Card>

          {/* Contact Info */}
          <div className="md:col-span-5 space-y-6">
            <Card className="p-6 sm:p-7 bg-white border border-stone-200 shadow-sm rounded-3xl">
              <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4">
                <Mail className="w-5 h-5 text-indigo-400" />
              </div>
              <h3 className="font-bold text-stone-900 mb-2 text-base">Atención directa</h3>
              <p className="text-xs sm:text-sm text-stone-500 leading-relaxed font-medium mb-4">
                Valoramos cada mensaje y sugerencia. Si tienes alguna duda sobre tu cuenta, facturación o funcionamiento, escríbenos directamente:
              </p>
              <a 
                href="mailto:soporte@capacitatu.com"
                className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold text-indigo-600 hover:text-indigo-700 active:scale-95 transition-all duration-150 ease-spring"
              >
                <span>soporte@capacitatu.com</span>
                <Send className="w-3.5 h-3.5" />
              </a>
            </Card>

            <div className="p-6 bg-stone-900 text-white rounded-3xl border border-stone-800 shadow-md">
              <h3 className="font-bold text-base mb-2">Compromiso de Privacidad</h3>
              <p className="text-xs text-stone-400 leading-relaxed font-medium">
                Tus datos de contacto se utilizan exclusivamente para responder a tu solicitud. Nunca compartimos tu correo ni enviamos publicidad no solicitada.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
